import React from 'react';
import type { GameReviewReport, MoveBadge } from '../game/game-review.ts';

interface GameReviewModalProps {
  report: GameReviewReport;
  onClose(): void;
  onViewReplay?(targetStep?: number): void;
}

export const BADGE_CONFIG: Record<MoveBadge, { label: string; icon: string; color: string }> = {
  brilliant: { label: 'Thiên tài', icon: '!!', color: '#00d2d3' },
  best: { label: 'Tối ưu', icon: '★', color: '#2ed573' },
  excellent: { label: 'Xuất sắc', icon: '✓', color: '#10ac84' },
  good: { label: 'Tốt', icon: '•', color: '#1dd1a1' },
  inaccuracy: { label: 'Thiếu sắc', icon: '?!', color: '#feca57' },
  mistake: { label: 'Sai lầm', icon: '?', color: '#ff9f43' },
  blunder: { label: 'Tai họa', icon: '??', color: '#ff4757' },
  forced: { label: 'Bắt buộc', icon: '□', color: '#a4b0be' },
};

export const GameReviewModal: React.FC<GameReviewModalProps> = ({
  report,
  onClose,
  onViewReplay,
}) => {
  const red = report.redPerformance;
  const black = report.blackPerformance;

  return (
    <div className="modal-backdrop review-modal-backdrop" onClick={onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="review-modal-header">
          <div className="review-badge-tag">📊 PHÂN TÍCH AI CHUYÊN SÂU</div>
          <h2>Báo cáo chất lượng ván đấu</h2>
          <span className="review-match-sub">
            {report.ruleset.toUpperCase()} · {report.totalPlies} nước đi · {report.date || ''}
          </span>
          <button className="review-close-btn" onClick={onClose} title="Đóng">
            ✕
          </button>
        </div>

        {/* Accuracy Comparison Hero */}
        <div className="review-hero-card">
          <div className="review-player-col red">
            <div className="player-indicator red">ĐỎ</div>
            <div className="player-name">{red.playerName}</div>
            <div className="accuracy-number red">{red.accuracy}%</div>
            <div className="accuracy-label">Độ chính xác</div>
            <div className="elo-estimate">~{red.ratingEstimate} Elo</div>
          </div>

          <div className="review-vs-divider">
            <div className="vs-circle">VS</div>
          </div>

          <div className="review-player-col black">
            <div className="player-indicator black">ĐEN</div>
            <div className="player-name">{black.playerName}</div>
            <div className="accuracy-number black">{black.accuracy}%</div>
            <div className="accuracy-label">Độ chính xác</div>
            <div className="elo-estimate">~{black.ratingEstimate} Elo</div>
          </div>
        </div>

        {/* Win Rate / Eval Balance Bar */}
        <div className="review-balance-bar-wrap">
          <div className="balance-label-row">
            <span>Đỏ: {red.accuracy > black.accuracy ? 'Ưu thế' : 'Bình ổn'}</span>
            <span>Tỷ lệ kiểm soát thế trận</span>
            <span>Đen: {black.accuracy > red.accuracy ? 'Ưu thế' : 'Bình ổn'}</span>
          </div>
          <div className="review-balance-bar">
            <div
              className="balance-fill red"
              style={{ width: `${Math.max(15, Math.min(85, (red.accuracy / (red.accuracy + black.accuracy)) * 100))}%` }}
            />
            <div
              className="balance-fill black"
              style={{ width: `${Math.max(15, Math.min(85, (black.accuracy / (red.accuracy + black.accuracy)) * 100))}%` }}
            />
          </div>
        </div>

        {/* Badges Breakdown Table */}
        <div className="review-badges-card">
          <h4>Thống kê phân loại nước đi</h4>
          <div className="badge-rows-list">
            {(['brilliant', 'best', 'excellent', 'good', 'inaccuracy', 'mistake', 'blunder'] as MoveBadge[]).map(
              (badge) => {
                const cfg = BADGE_CONFIG[badge] ?? BADGE_CONFIG.good;
                const redCount = red.badgeCounts[badge] || 0;
                const blackCount = black.badgeCounts[badge] || 0;
                if (redCount === 0 && blackCount === 0) return null;

                return (
                  <div key={badge} className="badge-stat-row">
                    <span className="badge-count-num red">{redCount}</span>
                    <div className="badge-name-pill" style={{ borderColor: cfg.color, color: cfg.color }}>
                      <span className="badge-icon-symbol">{cfg.icon}</span>
                      <span className="badge-text-label">{cfg.label}</span>
                    </div>
                    <span className="badge-count-num black">{blackCount}</span>
                  </div>
                );
              },
            )}
          </div>
        </div>

        {/* Critical Turning Point / Biggest Blunder Highlight */}
        {report.criticalTurningPoint && (
          <div className="review-turning-point-card">
            <div className="turning-point-header">
              <span className="turning-point-badge">⚡ BƯỚC NGOẶT QUYẾT ĐỊNH</span>
              <span className="turning-point-ply">
                Nước {report.criticalTurningPoint.moveNumber} ({report.criticalTurningPoint.side === 'red' ? 'Bên Đỏ' : 'Bên Đen'}): <strong>{report.criticalTurningPoint.notationVi}</strong>
              </span>
            </div>
            <div className="turning-point-body">
              <div className="turning-point-loss">
                Biến động: <span className="loss-value">Giảm {report.criticalTurningPoint.winRateLoss}% tỷ lệ thắng</span>
              </div>
              {report.criticalTurningPoint.whyWrong && (
                <div className="turning-point-detail wrong">
                  <span className="detail-tag">❌ Sai lầm:</span> {report.criticalTurningPoint.whyWrong}
                </div>
              )}
              {report.criticalTurningPoint.suggestedMoveVi && (
                <div className="turning-point-detail alt">
                  <span className="detail-tag">✅ Nước tối ưu:</span> <strong>{report.criticalTurningPoint.suggestedMoveVi}</strong>
                </div>
              )}
              {report.criticalTurningPoint.correctStrategy && (
                <div className="turning-point-detail strat">
                  <span className="detail-tag">💡 Chiến lược đúng:</span> {report.criticalTurningPoint.correctStrategy}
                </div>
              )}
              {onViewReplay && (
                <button
                  className="turning-point-action-btn"
                  onClick={() => onViewReplay(report.criticalTurningPoint!.ply)}
                >
                  🔍 Đến xem ngay nước cờ này trên bàn cờ (Nước {report.criticalTurningPoint.moveNumber})
                </button>
              )}
            </div>
          </div>
        )}

        {/* AI Coach Summary */}
        <div className="review-coach-card">
          <div className="coach-header">
            <span className="coach-avatar">🤖</span>
            <h4>Lời bình của Huấn luyện viên AI</h4>
          </div>
          <p className="coach-text">{report.executiveSummary}</p>

          {red.tacticalWeaknesses.length > 0 && (
            <div className="tactical-note red">
              <strong>Đỏ rút kinh nghiệm:</strong> {red.tacticalWeaknesses.join('; ')}
            </div>
          )}
          {black.tacticalWeaknesses.length > 0 && (
            <div className="tactical-note black">
              <strong>Đen rút kinh nghiệm:</strong> {black.tacticalWeaknesses.join('; ')}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="review-modal-actions">
          {onViewReplay && (
            <button className="review-action-btn primary" onClick={() => onViewReplay()}>
              🎬 Xem lại ván cờ & Phân tích từng nước
            </button>
          )}
          <button className="review-action-btn secondary" onClick={onClose}>
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};
